import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ChevronDown,
    ChevronUp,
    ArrowRight,
    Globe,
    Target,
    ShieldCheck,
    Coins,
    RefreshCw,
    CreditCard,
    GraduationCap,
    Flag,
    UserX,
    Siren,
    Activity,
    Landmark
} from 'lucide-react';
import PageHero from '../components/layout/PageHero';
import SEO from '../components/SEO';
import planData from '../data/plan_content.json';
import RichTextRenderer from '../components/content/RichTextRenderer';
import './ThePlan.css';

// Map sections to icons based on ID or index
const getSectionIcon = (id) => {
    const map = {
        'overview': Globe,
        'purpose': Target,
        'coverage': ShieldCheck,
        'financing': Coins,
        'transition': RefreshCw,
        'medical-debt': CreditCard,
        'workforce': GraduationCap,
        'veterans': Flag,
        'visitors': UserX,
        'fraud': Siren,
        'public-health': Activity,
        'governance': Landmark
    };
    // Fallback or partial match
    return map[id] || Target;
};

const PlanSection = ({ section, index }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isEven = index % 2 === 0;

    // Support either content array or markdown string
    let contentArray = section.content;
    if (!contentArray && section.markdown) {
        // Simple split by double newline for paragraphs
        contentArray = section.markdown.split(/\n\n+/);
    }
    contentArray = contentArray || [];

    // First paragraph is the "Pitch", rest is details
    const pitch = contentArray[0] || '';
    const details = contentArray.slice(1);

    return (
        <div className="blueprint-section-wrapper">
            {/* Main Row: Text + Image */}
            <motion.div
                id={section.id}
                className={`blueprint-section ${isEven ? 'layout-normal' : 'layout-reversed'}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.6 }}
            >
                {/* Text Side */}
                <div className="blueprint-text">
                    <h2>
                        {(() => {
                            const Icon = getSectionIcon(section.id);
                            return <Icon className="section-header-icon" size={28} />;
                        })()}
                        {section.title}
                    </h2>
                    <div className="blueprint-content-block">
                        <RichTextRenderer content={pitch} className="section-pitch" />

                        {details.length > 0 && (
                            <button
                                className="read-more-btn"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {isExpanded ? (
                                    <>Read Less <ChevronUp size={16} /></>
                                ) : (
                                    <>Read Full Details <ChevronDown size={16} /></>
                                )}
                            </button>
                        )}
                    </div>
                </div>

                {/* Image Side */}
                <div className="blueprint-visual">
                    <div className="image-frame glass-panel">
                        <img src={section.image} alt={section.title} />
                    </div>
                </div>
            </motion.div>

            {/* Full-Width Details Section Below */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="section-details-fullwidth"
                    >
                        <div className="details-content glass-panel">
                            <h3>Full Details</h3>
                            <div className="details-grid">
                                <RichTextRenderer content={details} />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default function ThePlan() {
    const [activeSection, setActiveSection] = useState('');

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // Track which section is currently in view
    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '-20% 0px -70% 0px', // Trigger when section is in the middle third of viewport
            threshold: 0
        };

        const observerCallback = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveSection(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        // Observe all sections
        planData.sections.forEach((section) => {
            const element = document.getElementById(section.id);
            if (element) {
                observer.observe(element);
            }
        });

        return () => observer.disconnect();
    }, []);

    return (
        <div className="blueprint-page">
            <SEO
                title="The SAFEC+ Plan - Healthcare for Everyone"
                description="Details of the SAFEC+ transformation. Universal coverage, fair financing, and an end to medical debt."
                image="/image/0_blueprint_hero.jpg"
            />
            {/* Hero */}
            <PageHero
                title="The National Blueprint"
                subtitle="The Secure Affordable Federal Essential Care Act Plus represents a fundamental shift in how we value health."
                image="/image/0_blueprint_hero.jpg"
                badge="Official Document"
            />

            <div className="container blueprint-main-layout">
                {/* Sticky Sidebar */}
                <aside className="blueprint-sidebar">
                    <div className="sidebar-content glass-panel">
                        <h3>Table of Contents</h3>
                        <nav>
                            <ul>
                                {planData.sections.map((section) => (
                                    <li key={section.id}>
                                        <button
                                            onClick={() => scrollToSection(section.id)}
                                            className={activeSection === section.id ? 'active' : ''}
                                        >
                                            {section.title.split('. ')[1] || section.title}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="blueprint-content-col">
                    {planData.sections.map((section, index) => (
                        <PlanSection key={section.id} section={section} index={index} />
                    ))}
                </main>
            </div>

            <div className="blueprint-footer">
                <h3>A Better System is Possible.</h3>
                <p>This is not a dream. It is a plan.</p>
            </div>
        </div>
    );
}
